/**
 * SQ-286 / SQ-562 photo markup: the marks a person puts on a photo, kept as
 * objects rather than pixels so a later edit can move, change or delete any
 * one of them, or reset the photo to its original.
 *
 * This is the web copy of Android's `core/photo/markup/Markup.kt`; both read
 * and write the same document (`stored_image.annotations`), so a photo marked
 * on one can be edited on the other. Change the two together;
 * `docs/fixtures/photo-markup-vectors.json` is run by both test suites.
 *
 * Coordinates: positions are NORMALIZED to the photo as rotated by
 * `rotation` (0..1 across its width / height); lengths are in units of the
 * photo's SHORT side, so neither changes meaning when the photo turns 90° or
 * is rendered at another resolution.
 *
 * Size and zoom: a mark comes out at the size its step shows on screen
 * whatever the zoom, so one added zoomed in is that much smaller on the photo
 * (a line's or text's `zoom`, a sticker's `scale`). From then on it is part of
 * the photo: it grows and shrinks with it.
 */

export type MarkColor = 'red' | 'blue' | 'yellow' | 'purple';
export type MarkSize = 's' | 'm' | 'l';

export const MARK_COLORS: MarkColor[] = ['red', 'blue', 'yellow', 'purple'];
export const MARK_SIZES: MarkSize[] = ['s', 'm', 'l'];

/** The four mark colors (Android's ARGB values). Red is the default. */
export const COLOR_HEX: Record<MarkColor, string> = {
  red: '#FF1744',
  blue: '#2979FF',
  yellow: '#FFEA00',
  purple: '#D500F9',
};

export const COLOR_NAME: Record<MarkColor, string> = {
  red: 'Red',
  blue: 'Blue',
  yellow: 'Yellow',
  purple: 'Purple',
};

export type StickerKind = 'arrow' | 'cut' | 'x' | 'circle' | 'sprinkler' | 'check' | 'cone' | 'hazard' | 'stop';

/**
 * `rotatable`: only the arrow, the cut line and the X can be turned; signs,
 * the sprinkler and the circle always stand upright. `inTray`: the check mark
 * was taken out of the tray (the sprinkler replaced it); checks saved by
 * earlier builds still open and draw.
 */
export const STICKERS: Record<StickerKind, {label: string; colorable: boolean; rotatable: boolean; inTray: boolean}> = {
  arrow: {label: 'Arrow', colorable: true, rotatable: true, inTray: true},
  cut: {label: 'Cut line', colorable: true, rotatable: true, inTray: true},
  x: {label: 'X', colorable: true, rotatable: true, inTray: true},
  circle: {label: 'Circle', colorable: true, rotatable: false, inTray: true},
  sprinkler: {label: 'Sprinkler', colorable: false, rotatable: false, inTray: true},
  check: {label: 'Check', colorable: true, rotatable: false, inTray: false},
  cone: {label: 'Cone', colorable: false, rotatable: false, inTray: true},
  hazard: {label: 'Hazard', colorable: false, rotatable: false, inTray: true},
  stop: {label: "Don't touch", colorable: false, rotatable: false, inTray: true},
};

/** Tray order, as on Android. */
export const STICKER_KINDS: StickerKind[] = ['arrow', 'cut', 'x', 'circle', 'sprinkler', 'check', 'cone', 'hazard', 'stop'];

/** A freehand line. `points` is flat: x0, y0, x1, y1, … (normalized). */
export interface StrokeMark {
  type: 'stroke';
  id: string;
  color: MarkColor;
  size: MarkSize;
  points: number[];
  /** The editor's zoom when it was drawn; its true width is its size's divided by this. */
  zoom: number;
}

/** A text box centered at (x, y), `width` wide (short-side units), turned `angle`° clockwise. */
export interface TextMark {
  type: 'text';
  id: string;
  color: MarkColor;
  size: MarkSize;
  x: number;
  y: number;
  width: number;
  angle: number;
  text: string;
  /** The editor's zoom when it was added; its true letter size is its size's divided by this. */
  zoom: number;
}

/** A ready-made symbol, `scale` (short-side units) across, centered. */
export interface StickerMark {
  type: 'sticker';
  id: string;
  kind: StickerKind;
  color: MarkColor;
  size: MarkSize;
  scale: number;
  x: number;
  y: number;
  angle: number;
}

export type LineKind = 'arrow' | 'cut';

/**
 * A straight mark between two free ends. The editor no longer adds these
 * (arrows and cut lines are stickers now); kept so marks saved by the first
 * SQ-286 builds still open, draw and can be moved or deleted.
 */
export interface LineMark {
  type: 'line';
  id: string;
  kind: LineKind;
  color: MarkColor;
  size: MarkSize;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export type Mark = StrokeMark | TextMark | StickerMark | LineMark;

export interface MarkupDoc {
  version: number;
  /**
   * The photo the marks sit on: normally the image's original. A photo edited
   * flat (by an older web editor) keeps that rendition as its base. Null only
   * for a doc never saved with one.
   */
  base: string | null;
  /** Clockwise quarter turns of `base`, in degrees: 0, 90, 180 or 270. */
  rotation: number;
  marks: Mark[];
}

export const emptyDoc = (base: string | null = null): MarkupDoc => ({version: 1, base, rotation: 0, marks: []});

/** Eight hex characters, as Android's (unique within one photo's marks is all it needs). */
export const newMarkId = (): string => {
  const bytes = new Uint8Array(4);
  const crypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (crypto?.getRandomValues) crypto.getRandomValues(bytes);
  else bytes.forEach((_, i) => (bytes[i] = Math.floor(Math.random() * 256)));
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

const pick = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  (allowed as readonly unknown[]).includes(value) ? value as T : fallback;
const num = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const str = (value: unknown, fallback: string): string => typeof value === 'string' ? value : fallback;

/** One mark from stored JSON, with Android's defaults; null for a kind this build does not know. */
const parseMark = (m: any): Mark | null => {
  if (!m || typeof m !== 'object') return null;
  const id = str(m.id, newMarkId());
  const color = pick(m.color, MARK_COLORS, 'red');
  const size = pick(m.size, MARK_SIZES, 'm');
  switch (m.type) {
    case 'stroke':
      return {
        type: 'stroke', id, color, size,
        points: Array.isArray(m.points) ? m.points.filter((p: unknown) => typeof p === 'number') : [],
        zoom: num(m.zoom, 1),
      };
    case 'text':
      return {
        type: 'text', id, color, size,
        x: num(m.x, 0.5), y: num(m.y, 0.5), width: num(m.width, 0.5), angle: num(m.angle, 0),
        text: str(m.text, ''), zoom: num(m.zoom, 1),
      };
    case 'sticker': {
      if (!(STICKER_KINDS as unknown[]).includes(m.kind)) return null;
      return {
        type: 'sticker', id, kind: m.kind, color, size,
        scale: num(m.scale, 0.2), x: num(m.x, 0.5), y: num(m.y, 0.5), angle: num(m.angle, 0),
      };
    }
    case 'line':
      return {
        type: 'line', id, kind: pick(m.kind, ['arrow', 'cut'] as const, 'arrow'), color, size,
        x1: num(m.x1, 0), y1: num(m.y1, 0), x2: num(m.x2, 0), y2: num(m.y2, 0),
      };
    default:
      return null;
  }
};

/** Parses a stored document; null for nothing, garbage or not an object (as Android). */
export const parseDoc = (json: string | null | undefined): MarkupDoc | null => {
  if (!json || !json.trim()) return null;
  try {
    const raw = JSON.parse(json);
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    return {
      version: num(raw.version, 1),
      base: typeof raw.base === 'string' ? raw.base : null,
      rotation: [0, 90, 180, 270].includes(raw.rotation) ? raw.rotation : 0,
      marks: (Array.isArray(raw.marks) ? raw.marks : []).map(parseMark).filter((m: Mark | null): m is Mark => m !== null),
    };
  } catch {
    return null;
  }
};

export const docToJson = (doc: MarkupDoc): string => JSON.stringify(doc);

/** A mark turned with the photo a quarter clockwise. */
export const rotateMarkClockwise = (m: Mark): Mark => {
  switch (m.type) {
    case 'stroke': {
      const points: number[] = [];
      for (let i = 0; i + 1 < m.points.length; i += 2) points.push(1 - m.points[i + 1], m.points[i]);
      return {...m, points};
    }
    case 'text':
      return {...m, x: 1 - m.y, y: m.x, angle: (m.angle + 90) % 360};
    case 'sticker':
      return {...m, x: 1 - m.y, y: m.x, angle: STICKERS[m.kind].rotatable ? (m.angle + 90) % 360 : 0};
    case 'line':
      return {...m, x1: 1 - m.y1, y1: m.x1, x2: 1 - m.y2, y2: m.x2};
  }
};

/** Turns the photo a quarter clockwise; every mark turns with it. */
export const rotateDocClockwise = (doc: MarkupDoc): MarkupDoc => ({
  ...doc,
  rotation: (doc.rotation + 90) % 360,
  marks: doc.marks.map(rotateMarkClockwise),
});

/** Value equality of two documents (undo steps, "is anything changed"). */
export const sameDoc = (a: MarkupDoc, b: MarkupDoc): boolean => a === b || docToJson(a) === docToJson(b);
